import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { useIntl } from "react-intl";
import moment from "moment";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { useSelector } from "react-redux";
import { useOrderServiceTypes } from "../../../hooks";
import { usePermission } from "../../../hooks/accessControl";
import OrderFormRows from "../../orderDetails/OrderFormRows";

const Order = (props) => {
  const {
    values,
    changeSelectedClient,
    branchId,
    setIsSupportingCommunity,
    setIsDayCenter,
    isSupportingCommunity,
    isDayCenter,
    setClientCategories,
    clientCategories,
    filterClientFrameSymbols,
    filterClientTariffSymbols,
    selectedClientTariffSymbol,
    selectedRevahaAuthority,
  } = props;
  const intl = useIntl();
  const orderServiceTypes = useOrderServiceTypes();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedClientCategory, setSelectedClientCategory] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const form = Form.useFormInstance();
  const serviceType =
    selectedClient &&
    (selectedClient.service_types.find((s) => s.id === values.service_type) ||
      selectedClient.service_types[0]);

  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );

  const filter = selectedClientCategory && {
    client_category_id: selectedClientCategory,
  };

  const scopePrefix = `branches/${branchId}/`;

  const onClientCategoriesChanged = (data) => {
    setClientCategories(data);
  };

  useEffect(() => {
    const serviceTypeId = values.order_service_type_id;
    const selectedOrderServiceType = orderServiceTypes.find(
      (i) => i.id === serviceTypeId
    );
    // Determine if the selected order service type is supporting community.
    const isSupporting = selectedOrderServiceType?.supporting_community;
    const dayCenter = selectedOrderServiceType?.day_center;

    // Update the state.
    setIsSupportingCommunity(isSupporting);
    setIsDayCenter(dayCenter);
  }, [orderServiceTypes, values?.order_service_type_id]);

  useEffect(() => {
    const filtered =
      selectedClientCategory &&
      clientCategories.filter((i) => i.id === selectedClientCategory)[0];
    setSelectedCategory(filtered);
  }, [selectedClientCategory, clientCategories]);
  useEffect(() => {
    const filtered = clients.filter((i) => i.id === values.client_id)[0];
    setSelectedClient(filtered);
    changeSelectedClient(filtered);
  }, [values.client_id, clients]);

  const onClientsChanged = (data) => {
    setClients(data);
  };

  const filterOrderServiceTypes = (items) => {
    const category = clientCategories.find(
      (c) => c.id === selectedCategory?.id
    );
    const isAllowedSupportingCommunity = isAllowedFeature(
      "supporting_community"
    );
    const isAllowedDayCenter = isAllowedFeature("day_center");

    if (!category) return [];

    const serviceTypeIds = new Set([
      ...(category.service_types || []).map((st) => st.id),
      ...(category.default_service_type_id
        ? [category.default_service_type_id]
        : []),
    ]);

    return items.filter((item) => {
      const isServiceTypeAllowed = serviceTypeIds.has(item.id);

      // If isAllowed is false, further filter out items where supporting_community is true
      const isSupportingCommunityAllowed =
        isAllowedSupportingCommunity ||
        (!isAllowedSupportingCommunity && !item.supporting_community);

      // If isAllowedDayCenter is false, further filter out items where day_center is true
      const isDayCenterAllowed =
        isAllowedDayCenter || (!isAllowedDayCenter && !item.day_center);

      return (
        isServiceTypeAllowed &&
        isSupportingCommunityAllowed &&
        isDayCenterAllowed
      );
    });
  };

  const filterContracts = (items) => {
    const clientContractsIds = selectedClient?.contracts
      ? selectedClient.contracts.map((item) => item.id)
      : [];
    return items.filter((i) => clientContractsIds.includes(i.id));
  };

  const getServiceTypeLabel = () => {
    if (!serviceType) {
      return "max_hours";
    }
    if (!serviceType.recurring) {
      return "max_hours";
    }
    if (serviceType.time_frame === 0) {
      return "hours_per_week";
    }
    return "hours_per_month";
  };

  const disabledDate = (current) => {
    return current && current <= moment(values.effective_from);
  };

  useEffect(() => {
    if (values?.client?.category_id && !selectedClientCategory) {
      setSelectedClientCategory(values?.client?.category_id);
    }
  }, [values, selectedClientCategory]);

  const onClientCategoryChange = (val) => {
    setSelectedClientCategory(val);
    form.setFieldValue("client_id", null);
    form.setFieldValue("service_type", null);
  };

  const disabledFromDate = (current) => {
    return (
      current &&
      values.effective_to &&
      current > moment(values.effective_to).endOf("day")
    );
  };
  const isTrom =
    selectedClient &&
    selectedClient.category &&
    selectedClient.category.is_trom === true;
  const hasEditorPermission = usePermission(branchId, "orders", "retro_update");

  return (
    <OrderFormRows
      form={form}
      clientCategories={clientCategories}
      orderIsUsed={false}
      onClientCategoriesChanged={onClientCategoriesChanged}
      filter={filter}
      selectedClient={selectedClient}
      hasEditorPermission={hasEditorPermission}
      getServiceTypeLabel={getServiceTypeLabel}
      serviceType={serviceType}
      startDate={values.effective_from}
      disabledFromDate={disabledFromDate}
      disabledDate={disabledDate}
      noEndDate={values.no_end_date}
      isTrom={isTrom}
      filterOrderServiceTypes={filterOrderServiceTypes}
      filterContracts={filterContracts}
      activeOrder={values}
      changedValues={values}
      isSupportingCommunity={isSupportingCommunity}
      isDayCenter={isDayCenter}
      type={"new"}
      scopePrefix={scopePrefix}
      activePatient={activePatient}
      intl={intl}
      onClientCategoryChange={onClientCategoryChange}
      onClientsChanged={onClientsChanged}
      filterClientFrameSymbols={filterClientFrameSymbols}
      filterClientTariffSymbols={filterClientTariffSymbols}
      selectedClientTariffSymbol={selectedClientTariffSymbol}
      selectedRevahaAuthority={selectedRevahaAuthority}
    />
  );
};

export default Order;
